// Diálogo de confirmação do design system (no lugar de window.confirm).
// Uso: const confirm = useConfirm(); if (!(await confirm({ title: 'Apagar?', message: '...', danger: true }))) return
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

export interface ConfirmOptions {
  title: string
  message?: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  /** Ação destrutiva: botão vermelho */
  danger?: boolean
}
type Pending = ConfirmOptions & { resolve: (ok: boolean) => void }

const ConfirmContext = createContext<(o: ConfirmOptions) => Promise<boolean>>(() => Promise.resolve(false))

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null)
  const confirm = useCallback((o: ConfirmOptions) => new Promise<boolean>((resolve) => setPending({ ...o, resolve })), [])
  const close = useCallback((ok: boolean) => { setPending((p) => { p?.resolve(ok); return null }) }, [])
  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending && <ConfirmDialog {...pending} onClose={close} />}
    </ConfirmContext.Provider>
  )
}

export const useConfirm = () => useContext(ConfirmContext)

function ConfirmDialog({ title, message, confirmLabel = 'Confirmar', cancelLabel = 'Cancelar', danger, onClose }: ConfirmOptions & { onClose: (ok: boolean) => void }) {
  const cancelRef = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const before = document.activeElement as HTMLElement | null
    cancelRef.current?.focus() // o foco começa em "Cancelar": um Enter sem querer não apaga nada
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(false) }
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('keydown', onKey); before?.focus?.() }
  }, [onClose])
  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(false) }}>
      <div className="modal" role="alertdialog" aria-modal="true" aria-labelledby="confirm-title" aria-describedby={message ? 'confirm-msg' : undefined}>
        <h2 id="confirm-title">{title}</h2>
        {message && <p id="confirm-msg" className="muted mt-8">{message}</p>}
        <div className="row mt-16" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button ref={cancelRef} type="button" className="btn" onClick={() => onClose(false)}>{cancelLabel}</button>
          <button type="button" className={`btn primary${danger ? ' danger' : ''}`} onClick={() => onClose(true)}>{confirmLabel}</button>
        </div>
      </div>
    </div>
  )
}
